import React from 'react';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { CONTACT } from '../config';
import { BRAND } from '../utils/brand';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, ClosingCta, usePrimaryCta } from '../components/common/B2B';
import { Section, ItemsSection, HeroChecks, TextLink } from '../components/common/B2BSections';
import ClientLogos from '../components/common/ClientLogos.jsx';

// Quiénes somos (nov-2026), rediseño B2B con el contenido anterior (namespace
// i18n «about», que queda sin uso) condensado: especialización, lo que nos
// diferencia, la cita de Jesús (literal), cómo trabajamos, los clientes (logos
// solo en B/N y pequeños, condición de su permiso), el grupo Cadenia y los datos
// de la empresa (los del aviso legal). ENS con la fórmula de OCA, sin categoría.

const PAGE_URL = { es: 'https://www.bluecmr.com/quienes-somos', en: 'https://www.bluecmr.com/en/about-us' };

const COPY = {
  es: {
    seoTitle: 'Quiénes somos: especialistas en documentos de transporte | BlueCMR',
    seoDescription: 'BLUECMR, S.L.U., del grupo Cadenia, desarrolla desde Ponferrada (León) una plataforma dedicada solo a los documentos de transporte por carretera.',
    seoKeywords: 'bluecmr, quiénes somos, documentos de transporte digitales, cadenia, ponferrada',
    heroEyebrow: 'Quiénes somos',
    heroTitle: 'Especialistas en documentos de transporte digitales',
    heroText: 'Desde Ponferrada (León), BLUECMR, S.L.U. desarrolla una plataforma dedicada solo a los documentos de transporte por carretera, para cargadores, transportistas y proveedores de software del transporte nacional e internacional.',
    heroChecks: ['Grupo Cadenia', 'Certificación ENS', 'Datos en la Unión Europea'],
    diff: {
      eyebrow: 'Lo que nos diferencia',
      title: 'Tecnología, normativa y sector, en el mismo equipo',
      items: [
        { title: 'Tecnología', text: 'Software empresarial, trazabilidad en tiempo real y arquitectura serverless en AWS, con plataformas SaaS diseñadas para crecer.' },
        { title: 'Normativa', text: 'Formación jurídica en la normativa española y europea del transporte (eIDAS, Ley de Movilidad Sostenible, eFTI, Protocolo eCMR) para anticipar sus requisitos y cumplirlos desde el diseño.' },
        { title: 'Sector', text: 'Conocimiento directo del transporte de mercancías por carretera y años de trabajo con empresas logísticas de España, Francia, Italia y Portugal.' },
      ],
    },
    quote: 'El sector del transporte necesita herramientas que entiendan tanto la normativa como la realidad del día a día. Por eso creamos BlueCMR: cada funcionalidad combina cumplimiento legal, usabilidad práctica y tecnología moderna.',
    quoteName: 'Jesús Santín',
    quoteRole: 'Director de BlueCMR',
    quoteAlt: 'Jesús Santín, fundador de BlueCMR',
    how: {
      eyebrow: 'Cómo trabajamos',
      title: 'Solo documentos de transporte, y bien hechos',
      items: [
        { title: 'Cumplimiento desde el diseño', text: 'Cada función responde a los requisitos técnicos y legales del transporte europeo, los de hoy y los que ya están aprobados.' },
        { title: 'Especialización', text: 'BlueCMR está hecho desde cero para documentos de transporte: no es una herramienta genérica adaptada.' },
        { title: 'Infraestructura de empresa', text: 'Serverless en AWS, en las regiones de Aragón y París, con datos estructurados según eFTI. BLUECMR, S.L.U. dispone de un sistema de gestión certificado de acuerdo al RD 311/2022 (ENS) por OCA CERT.' },
      ],
    },
    clients: 'Algunas de las empresas que trabajan con BlueCMR',
    groupEyebrow: 'Grupo Cadenia',
    groupTitle: 'Parte del grupo Cadenia',
    groupText: 'BlueCMR y Cadenia forman parte del mismo grupo empresarial. Cadenia está especializada en visibilidad en tiempo real e inteligencia aplicada a la cadena de suministro; BlueCMR nace con un foco exclusivo en la documentación digital del transporte, con los mismos estándares de calidad.',
    groupLogoAlt: 'Cadenia, inteligencia para la cadena de suministro',
    dataTitle: 'Datos de la empresa',
    data: [
      ['Razón social', BRAND.legalName],
      ['CIF', 'B26654517'],
      ['Domicilio social', 'Calle Fueros de León 1, 24402 Ponferrada (León)'],
      ['Teléfono', CONTACT.phoneDisplay],
      ['Correo', CONTACT.email],
    ],
    legalLink: 'Aviso legal',
    closing: '¿Hablamos?',
    closingText: 'Si está valorando cómo digitalizar sus documentos de transporte, le enseñamos cómo encaja BlueCMR en su operativa.',
    breadcrumb: ['Inicio', 'Quiénes somos'],
  },
  en: {
    seoTitle: 'About us: specialists in digital transport documents | BlueCMR',
    seoDescription: 'BLUECMR, S.L.U., part of the Cadenia group, builds from Ponferrada (Spain) a platform dedicated solely to road transport documents.',
    seoKeywords: 'bluecmr, about us, digital transport documents, cadenia',
    heroEyebrow: 'About us',
    heroTitle: 'Specialists in digital transport documents',
    heroText: 'From Ponferrada (León, Spain), BLUECMR, S.L.U. builds a platform dedicated solely to road transport documents, for shippers, carriers and software providers in domestic and international transport.',
    heroChecks: ['Cadenia group', 'ENS certification', 'Data in the European Union'],
    diff: {
      eyebrow: 'What sets us apart',
      title: 'Technology, regulation and industry, in one team',
      items: [
        { title: 'Technology', text: 'Enterprise software, real-time traceability and serverless architecture on AWS, with SaaS platforms designed to scale.' },
        { title: 'Regulation', text: 'Legal training in Spanish and European transport regulation (eIDAS, the Sustainable Mobility Law, eFTI, the eCMR Protocol) to anticipate its requirements and meet them by design.' },
        { title: 'Industry', text: 'First-hand knowledge of road freight transport and years of work with logistics companies in Spain, France, Italy and Portugal.' },
      ],
    },
    quote: 'The transport sector needs tools that understand both the regulations and day-to-day reality. That is why we created BlueCMR: every feature combines legal compliance, practical usability and modern technology.',
    quoteName: 'Jesús Santín',
    quoteRole: 'Director of BlueCMR',
    quoteAlt: 'Jesús Santín, founder of BlueCMR',
    how: {
      eyebrow: 'How we work',
      title: 'Only transport documents, done properly',
      items: [
        { title: 'Compliance by design', text: 'Every feature meets the technical and legal requirements of European transport, both current ones and those already adopted.' },
        { title: 'Specialisation', text: 'BlueCMR is built from scratch for transport documents: it is not a generic tool adapted to them.' },
        { title: 'Enterprise infrastructure', text: 'Serverless on AWS, in the Aragon and Paris regions, with data structured according to eFTI. BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (ENS) by OCA CERT.' },
      ],
    },
    clients: 'Some of the companies working with BlueCMR',
    groupEyebrow: 'Cadenia group',
    groupTitle: 'Part of the Cadenia group',
    groupText: 'BlueCMR and Cadenia belong to the same business group. Cadenia specialises in real-time visibility and intelligence applied to the supply chain; BlueCMR was born with an exclusive focus on digital transport documentation, sharing the same quality standards.',
    groupLogoAlt: 'Cadenia, supply chain intelligence',
    dataTitle: 'Company details',
    data: [
      ['Company name', BRAND.legalName],
      ['Tax ID (CIF)', 'B26654517'],
      ['Registered office', 'Calle Fueros de León 1, 24402 Ponferrada (León), Spain'],
      ['Phone', CONTACT.phoneDisplay],
      ['Email', CONTACT.email],
    ],
    legalLink: 'Legal notice',
    closing: 'Shall we talk?',
    closingText: 'If you are considering how to digitise your transport documents, we will show you how BlueCMR fits your operations.',
    breadcrumb: ['Home', 'About us'],
  },
};

const QuienesSomosPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: PAGE_URL[lang] },
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'BlueCMR',
            legalName: BRAND.legalName,
            url: 'https://www.bluecmr.com',
            logo: 'https://www.bluecmr.com/og/logo-bluecmr.png',
            description: c.seoDescription,
            founder: { '@type': 'Person', name: 'Jesús Santín' },
            address: { '@type': 'PostalAddress', streetAddress: 'Calle Fueros de León 1', postalCode: '24402', addressLocality: 'Ponferrada', addressRegion: 'León', addressCountry: 'ES' },
            telephone: CONTACT.phoneDisplay,
            email: CONTACT.email,
          },
        ]}
      />

      <div className="bg-white">
        <PageHero eyebrow={c.heroEyebrow} title={c.heroTitle} text={c.heroText} primary={primaryCta} compact>
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        <ItemsSection {...c.diff} tone="white" />

        {/* Cita de Jesús */}
        <Section tone="gray">
          <figure className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="md:col-span-3">
              <img src="/fonts/jesus-santin.jpg" alt={c.quoteAlt} width="192" height="192" className="w-40 h-40 md:w-48 md:h-48 object-cover ring-1 ring-slate-200" loading="lazy" />
            </div>
            <div className="md:col-span-9">
              <blockquote className="text-xl md:text-2xl text-slate-800 leading-relaxed">«{c.quote}»</blockquote>
              <figcaption className="mt-6">
                <span className="block font-semibold text-slate-900">{c.quoteName}</span>
                <span className="block text-slate-600">{c.quoteRole}</span>
              </figcaption>
            </div>
          </figure>
        </Section>

        <ItemsSection {...c.how} tone="white" />

        <div className="border-t border-slate-200">
          <ClientLogos title={c.clients} />
        </div>

        {/* Grupo Cadenia y datos de la empresa */}
        <Section tone="gray">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <SectionIntro eyebrow={c.groupEyebrow} title={c.groupTitle} className="mb-6" />
              <p className="text-slate-600 leading-relaxed">{c.groupText}</p>
              <a href="https://cadenia.eu" target="_blank" rel="noopener noreferrer" className="mt-8 inline-block opacity-70 hover:opacity-100 transition-opacity">
                <img src="/logo-cadenia-300x100.svg" alt={c.groupLogoAlt} className="h-12" />
              </a>
            </div>
            <div className="lg:col-span-5">
              <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-4">{c.dataTitle}</h2>
              <dl className="border-t border-slate-300 text-[15px]">
                {c.data.map(([k, v]) => (
                  <div key={k} className="grid grid-cols-5 gap-4 border-b border-slate-200 py-3">
                    <dt className="col-span-2 text-slate-500">{k}</dt>
                    <dd className="col-span-3 text-slate-900">{v}</dd>
                  </div>
                ))}
              </dl>
              <TextLink to={getLocalizedUrl('/aviso-legal')} className="mt-6">{c.legalLink}</TextLink>
            </div>
          </div>
        </Section>

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default QuienesSomosPage;
